/* eslint-disable */
// @ts-nocheck

import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Box,
  FormControl,
  Typography,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormGroup,
  Divider,
  Grid,
} from '@mui/material';
import { toast } from 'react-toastify';
import { useIntl } from 'react-intl';
import { useHistory, useParams } from 'react-router-dom';
import { useTypedSelector } from 'helpers';
import { hasWriteAccessToMasterConfigSubSystem, hasWriteAccessToFaresSubSystem, hasWriteAccessToOperationsSubSystem } from 'selectors';

/**
 * EditEntity - Reusable component for editing entities with dynamic inputs and dropdowns
 * 
 * @param {Object} props
 * @param {string} props.pageTitle - Translation key for page title (e.g., 'EditBank')
 * @param {string} props.fetchApiUrl - API URL for fetching entity data (can include :id, :org_id, :bank_id placeholders)
 * @param {string} props.fetchApiMethod - HTTP method for fetch API (default: 'GET')
 * @param {Function} props.buildFetchPayload - Function to build fetch payload (for POST requests)
 * @param {string} props.dataPath - Path to data in response (default: 'data.data')
 * @param {Array} props.inputs - Array of input field configurations
 * @param {Array} props.dropdowns - Array of dropdown field configurations
 * @param {Array} props.additionalApis - Array of additional API calls to make after main fetch
 * @param {string} props.updateApiUrl - API URL for updating the entity
 * @param {string} props.updateApiMethod - HTTP method for update API (default: 'PATCH')
 * @param {Function} props.buildUpdatePayload - Function to build the update payload from form values
 * @param {string} props.backUrl - URL to navigate back to (can include :id placeholder)
 * @param {string} props.successMessage - Translation key for success message
 * @param {string} props.updateButtonLabel - Translation key for update button label
 * @param {Function} props.onDataFetched - Callback after data is fetched (data, additionalData) => void
 * @param {string} props.subsystemName - Name of subsystem for permission check (optional)
 * @param {string} props.permissionSystem - Permission system to use: 'masterConfig', 'fares', 'operations' (default: 'masterConfig')
 * @param {string} props.noAccessMessage - Translation key for no access message (optional)
 * 
 * Input Configuration:
 * {
 *   name: string,              // Field name/key in fetched data
 *   labelKey: string,          // Translation key for label
 *   type: string,              // Input type (text, number, email, etc.)
 *   required: boolean,         // Is field required
 *   disabled: boolean,         // Is field disabled
 *   maxLength: number,         // Optional native maxLength — mirror the DB column width
 *                              //   so the user is stopped before the API has to reject it
 *   minLength: number,         // Optional native minLength
 * }
 * 
 * Dropdown Configuration:
 * {
 *   name: string,              // Field name/key in fetched data
 *   labelKey: string,          // Translation key for label
 *   required: boolean,         // Is field required
 *   disabled: boolean,         // Is field disabled
 *   apiUrl: string | Function, // API URL to fetch options OR function that returns URL based on formValues
 *   apiMethod: string,         // 'GET' or 'POST'
 *   apiPayload: Object,        // Payload for POST requests (optional)
 *   dataPath: string,          // Path to data in response (default: 'data.data')
 *   dependsOn: string,         // Name of another field this dropdown depends on
 *   optionConfig: {            // Configuration for rendering options
 *     valueKey: string,        // Key for option value
 *     labelKey: string,        // Key for option label (English)
 *     labelKeyAr: string       // Key for option label (Arabic)
 *   },
 *   filterOptions: Function,   // Optional function to filter/transform options (options, entityData, intl) => filteredOptions
 * }
 * 
 * Additional API Configuration:
 * {
 *   name: string,              // Name for storing the result
 *   url: string | Function,    // API URL or function that returns URL based on mainData
 *   method: string,            // HTTP method (default: 'GET')
 *   payload: Object | Function,// Payload or function that returns payload based on mainData
 *   dataPath: string,          // Path to data in response (default: 'data.data')
 *   onSuccess: Function,       // Callback after successful fetch (data, mainData) => void
 * }
 */
const EMPTY_ARRAY = [];

// Detect Arabic text and return matching toast style
const isArabic = (str) => /[\u0600-\u06FF]/.test(str || '');
const toastDir = (msg) => ({
  style: {
    direction: isArabic(msg) ? 'rtl' : 'ltr',
    textAlign: isArabic(msg) ? 'right' : 'left',
    fontFamily: 'Cairo, sans-serif',
    fontWeight: 'bold',
  },
});

const EditEntity = ({
  pageTitle,
  fetchApiUrl,
  fetchApiMethod = 'GET',
  buildFetchPayload,
  dataPath = 'data.data',
  inputs = EMPTY_ARRAY,
  dropdowns = EMPTY_ARRAY,
  additionalApis = EMPTY_ARRAY,
  updateApiUrl,
  updateApiMethod = 'PATCH',
  buildUpdatePayload,
  backUrl,
  successMessage = 'UpdatedSuccessfully',
  updateButtonLabel = 'update',
  onDataFetched,
  subsystemName,
  permissionSystem = 'masterConfig',
  noAccessMessage = 'YouDontHavePermissionToAccessThisPage',
  itemsPerColumn: propItemsPerColumn,
  renderAboveSubmit,
  renderAboveFields,
  messageTransformer,
  beforeSubmit,
}) => {
  const intl = useIntl();
  const history = useHistory();
  const { id, org_id, bank_id } = useParams();
  const currentLanguageCode = localStorage.getItem("EASY_MOBILITY_LANGUAGE") || 'en-us';
  
  // Check write access if subsystem is provided
  const hasWriteAccess = subsystemName
    ? (() => {
        switch (permissionSystem) {
          case 'fares':
            return useTypedSelector(state => hasWriteAccessToFaresSubSystem(state, subsystemName));
          case 'operations':
            return useTypedSelector(state => hasWriteAccessToOperationsSubSystem(state, subsystemName));
          case 'masterConfig':
          default:
            return useTypedSelector(state => hasWriteAccessToMasterConfigSubSystem(state, subsystemName));
        }
      })()
    : true;
  
  // Get came_from query parameter — read from window.location to avoid stale history.location on first render
  const came_from = new URLSearchParams(window.location.search).get('came_from');
  
  // Auth data
  let userAuthData = '';
  const authData = localStorage.getItem("authData");
  if (authData !== null) {
    const parsedAuthData = JSON.parse(authData);
    userAuthData = parsedAuthData.userToken;
  }

  // Dynamic state for inputs
  const [formValues, setFormValues] = useState({});
  const [entityData, setEntityData] = useState(null);
  const [additionalData, setAdditionalData] = useState({});
  
  // Dynamic state for dropdowns
  const [dropdownOptions, setDropdownOptions] = useState({});
  const [dropdownLoading, setDropdownLoading] = useState({});
  
  // General state
  const [isFormLoading, setIsFormLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [focusedField, setFocusedField] = useState(null);
  const [hoveredHint, setHoveredHint] = useState(null);

  // Stabilize inputs/dropdowns so inline [] props don't trigger re-fetches on every render
   
  const stableInputs = useMemo(() => inputs, [JSON.stringify(inputs.map(i => ({ name: i.name, type: i.type, apiUrl: i.apiUrl, dependsOn: i.dependsOn })))]);
   
  const stableDropdowns = useMemo(() => dropdowns, [JSON.stringify(dropdowns.map(d => ({ name: d.name, apiUrl: d.apiUrl, dependsOn: d.dependsOn })))]);

  // Fetch entity data
  useEffect(() => {
    const fetchEntity = async () => {
      setIsFormLoading(true);
      setError(null);
      
      try {
        // Replace placeholders in URL
        let url = fetchApiUrl
          .replace(':id', id)
          .replace(':org_id', org_id)
          .replace(':bank_id', bank_id);
        
        if (!url.startsWith('http')) {
          url = `${process.env.REACT_APP_ABT_ALL_APIS_BASE_URL}${url}`;
        }

        const config = {
          headers: {
            'access-token': userAuthData?.['access-token'] || userAuthData?.access_token,
            'uid': userAuthData?.uid,
            'client': userAuthData?.client,
          },
        };

        let response;
        if (fetchApiMethod.toUpperCase() === 'POST') {
          const payload = buildFetchPayload ? buildFetchPayload({ id, org_id, bank_id }) : {};
          response = await axios.post(url, payload, config);
        } else {
          response = await axios.get(url, config);
        }

        // Extract data based on dataPath
        const pathParts = dataPath.split('.');
        let data = response;
        
        for (const part of pathParts) {
          data = data[part];
        }

        console.log('[EditEntity] Fetched data:', data);
        setEntityData(data);

        // Fetch additional APIs if configured (before initializing form values)
        if (additionalApis.length > 0) {
          await fetchAdditionalApis(data);
        }

        // Call onDataFetched callback if provided (allows transforming data before form initialization)
        if (onDataFetched) {
          onDataFetched(data, additionalData);
        }

        // Initialize form values from fetched data
        const initialValues = {};
        inputs.forEach(input => {
          // Handle language-specific values
          if (input.useLanguageValue && input.enKey && input.arKey) {
            const langKey = currentLanguageCode === 'en-us' ? input.enKey : input.arKey;
            initialValues[input.name] = data[langKey] || '';
          } else {
            initialValues[input.name] = data[input.name] !== undefined ? data[input.name] : (input.defaultValue || '');
          }

          // Also initialize fields within checkboxGroup options
          if (input.type === 'checkboxGroup' && input.options) {
            input.options.forEach(opt => {
              initialValues[opt.name] = data[opt.name] !== undefined ? data[opt.name] : (opt.defaultValue || false);
            });
          }
        });
        dropdowns.forEach(dropdown => {
          initialValues[dropdown.name] = data[dropdown.name] !== undefined ? data[dropdown.name] : (dropdown.defaultValue || '');
        });
        console.log('[EditEntity] Initial form values:', initialValues);
        setFormValues(initialValues);
      } catch (error) {
        const rawMsg = error?.response?.data?.message || error.toString();
        const transformedMsg = messageTransformer ? messageTransformer(rawMsg) : rawMsg;
        const errorMsg = `Failed to fetch entity: ${transformedMsg}`;
        setError(errorMsg);
      toast.error(errorMsg, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        ...toastDir(error?.response?.data?.message || error.toString()),
      });
      } finally {
        setIsFormLoading(false);
      }
    };

    fetchEntity();
  }, [id, org_id, bank_id]);

  // Fetch additional APIs
  const fetchAdditionalApis = async (mainData) => {
    const results = {};
    
    for (const apiConfig of additionalApis) {
      try {
        // Resolve URL
        let url = typeof apiConfig.url === 'function'
          ? apiConfig.url(mainData)
          : apiConfig.url;
        
        url = `${process.env.REACT_APP_ABT_ALL_APIS_BASE_URL}${url}`;

        const config = {
          headers: {
            'access-token': userAuthData?.['access-token'] || userAuthData?.access_token,
            'uid': userAuthData?.uid,
            'client': userAuthData?.client,
          },
        };

        let response;
        const method = (apiConfig.method || 'GET').toUpperCase();
        
        if (method === 'POST') {
          const payload = typeof apiConfig.payload === 'function'
            ? apiConfig.payload(mainData)
            : apiConfig.payload || {};
          response = await axios.post(url, payload, config);
        } else {
          response = await axios.get(url, config);
        }

        // Extract data based on dataPath
        const apiDataPath = apiConfig.dataPath || 'data.data';
        const pathParts = apiDataPath.split('.');
        let data = response;
        
        for (const part of pathParts) {
          data = data[part];
        }

        console.log(`[EditEntity] Additional API ${apiConfig.name}:`, data);
        results[apiConfig.name] = data;

        // Call onSuccess callback if provided
        if (apiConfig.onSuccess) {
          apiConfig.onSuccess(data, mainData);
        }
      } catch (error) {
        console.error(`[EditEntity] Error fetching ${apiConfig.name}:`, error);
        results[apiConfig.name] = null;
      }
    }

    setAdditionalData(results);
  };

  // Fetch dropdown options
  useEffect(() => {
    if (!isFormLoading) {
      const allDropdowns = [
          ...stableDropdowns,
          ...stableInputs.filter(i => i.type === 'dropdown')
      ];

      allDropdowns.forEach(dropdown => {
        // Only fetch if dropdown doesn't depend on another field
        if (dropdown.apiUrl && !dropdown.dependsOn) {
          fetchDropdownOptions(dropdown);
        }
      });
    }
  }, [isFormLoading, stableDropdowns, stableInputs]);

  // Fetch dependent dropdown options when dependency changes
  useEffect(() => {
    if (!isFormLoading) {
      const allDropdowns = [
          ...stableDropdowns,
          ...stableInputs.filter(i => i.type === 'dropdown')
      ];

      allDropdowns.forEach(dropdown => {
        if (dropdown.dependsOn && formValues[dropdown.dependsOn]) {
          fetchDropdownOptions(dropdown);
        }
      });
    }
    // Only re-run when the specific dependency values change, not all formValues
     
  }, [
    ...[...stableDropdowns, ...stableInputs.filter(i => i.type === 'dropdown')].filter(d => d.dependsOn).map(d => formValues[d.dependsOn]),
    isFormLoading,
    stableDropdowns,
    stableInputs
  ]);

  const fetchDropdownOptions = async (dropdown) => {
    setDropdownLoading(prev => ({ ...prev, [dropdown.name]: true }));
    
    try {
      const config = {
        headers: {
          'access-token': userAuthData?.['access-token'] || userAuthData?.access_token,
          'uid': userAuthData?.uid,
          'client': userAuthData?.client,
        },
      };

      // Resolve API URL (can be string or function)
      let apiUrl = typeof dropdown.apiUrl === 'function'
        ? dropdown.apiUrl(formValues, dropdownOptions)
        : dropdown.apiUrl;

      // Add base URL if not already present
      if (!apiUrl.startsWith('http')) {
        apiUrl = `${process.env.REACT_APP_ABT_ALL_APIS_BASE_URL}${apiUrl}`;
      }

      let response;
      if (dropdown.apiMethod?.toUpperCase() === 'POST') {
        response = await axios.post(
          apiUrl,
          dropdown.apiPayload || {},
          config
        );
      } else {
        response = await axios.get(apiUrl, config);
      }

      // Extract data based on dataPath (default: 'data.data')
      const dropdownDataPath = dropdown.dataPath || 'data.data';
      const pathParts = dropdownDataPath.split('.');
      let data = response;
      
      for (const part of pathParts) {
        data = data[part];
      }

      console.log(`[EditEntity] Dropdown: ${dropdown.name}`, data);

      // Ensure data is always an array
      let arrayData = Array.isArray(data) ? data : [];
      
      // Apply filterOptions if provided
      if (dropdown.filterOptions && typeof dropdown.filterOptions === 'function') {
        arrayData = dropdown.filterOptions(arrayData, entityData, intl);
        console.log(`[EditEntity] Filtered options for ${dropdown.name}:`, arrayData);
      }
      
      setDropdownOptions(prev => ({ ...prev, [dropdown.name]: arrayData }));
    } catch (error) {
      console.error(`[EditEntity] Error fetching ${dropdown.name}:`, error);
      toast.error(`Failed to fetch ${intl.formatMessage({ id: dropdown.labelKey })} options`, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        ...toastDir(`Failed to fetch ${intl.formatMessage({ id: dropdown.labelKey })} options`),
      });
      setDropdownOptions(prev => ({ ...prev, [dropdown.name]: [] }));
    } finally {
      setDropdownLoading(prev => ({ ...prev, [dropdown.name]: false }));
    }
  };

  const handleInputChange = (name, value) => {
    console.log(`[EditEntity] handleInputChange - name: ${name}, value:`, value);
    setFormValues(prev => {
      const newValues = { ...prev, [name]: value };
      console.log(`[EditEntity] Updated formValues:`, newValues);
      
      // Reset dependent dropdowns when dependency changes
      dropdowns.forEach(dropdown => {
        if (dropdown.dependsOn === name) {
          newValues[dropdown.name] = '';
          // Clear options for dependent dropdown
          setDropdownOptions(prevOptions => ({ ...prevOptions, [dropdown.name]: [] }));
        }
      });
      
      return newValues;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      let currentValues = formValues;
      let effectiveBackUrl = backUrl;
      if (beforeSubmit) {
        const result = await beforeSubmit(formValues);
        if (!result.proceed) {
          setIsLoading(false);
          return;
        }
        if (result.updatedValues) {
          currentValues = result.updatedValues;
        }
        if (result.backUrl) {
          effectiveBackUrl = result.backUrl;
        }
      }

      const payload = buildUpdatePayload
        ? buildUpdatePayload(currentValues, { id, org_id, bank_id }, entityData, dropdownOptions)
        : currentValues;
      
      // Replace placeholders in URL
      let url = updateApiUrl
        .replace(':id', id)
        .replace(':org_id', org_id)
        .replace(':bank_id', bank_id);
      
      let finalUrl = url;
      if (!finalUrl.startsWith('http')) {
        finalUrl = `${process.env.REACT_APP_ABT_ALL_APIS_BASE_URL}${finalUrl}`;
      }

      const config = {
        headers: {
          'access-token': userAuthData?.['access-token'] || userAuthData?.access_token,
          'uid': userAuthData?.uid,
          'client': userAuthData?.client,
        },
      };

      let response;
      const method = updateApiMethod.toUpperCase();
      
      
      if (method === 'PATCH') {
        response = await axios.patch(finalUrl, payload, config);
      } else if (method === 'PUT') {
        response = await axios.put(finalUrl, payload, config);
      } else if (method === 'POST') {
        response = await axios.post(finalUrl, payload, config);
      }

      toast.success(intl.formatMessage({ id: successMessage }), {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        toastStyle: {
          backgroundColor: '#F1662D',
          color: 'white',
        },
      });

      // Navigate back
      let navigateUrl = effectiveBackUrl
        .replace(':id', id)
        .replace(':org_id', org_id)
        .replace(':bank_id', bank_id);

      // Navigate back using the provided backUrl (already replaced placeholders)
      history.push(navigateUrl);
    } catch (error) {
      const rawMessage = error?.response?.data?.message || error.toString();
      const errorMessage = messageTransformer ? messageTransformer(rawMessage) : rawMessage;
      setError(errorMessage);
      toast.error(`${errorMessage}`, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        ...toastDir(errorMessage),
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Combine inputs and dropdowns into a single array for column layout
  // We keep them separate for backward compatibility, but allow mixing
  const allFields = [
    ...inputs.filter(input => input.inputType !== 'hidden').map(input => ({ ...input, type: input.type || 'input' })),
    ...dropdowns.map(dropdown => ({ ...dropdown, type: 'dropdown' }))
  ];

  // Hidden inputs (render separately, not in columns)
  const hiddenFields = inputs.filter(input => input.inputType === 'hidden');

  // Modified column logic: Allow specific grid layout if provided
  const columns = [];
  const itemsPerColumn = propItemsPerColumn || 5; // Default to 5
  for (let i = 0; i < allFields.length; i += itemsPerColumn) {
    columns.push(allFields.slice(i, i + itemsPerColumn));
  }

  // Check permission before rendering form
  if (!hasWriteAccess) {
    return (
      <Box sx={{ marginTop: -5, padding: 4 }}>
        <Typography sx={{ color: '#252733', fontWeight: 'bold', marginBottom: '16px' }}>
          {intl.formatMessage({ id: pageTitle })}
        </Typography>
        <Box sx={{ 
          padding: 4, 
          textAlign: 'center',
          backgroundColor: '#f5f5f5',
          borderRadius: '8px',
          marginTop: 4
        }}>
          <Typography variant="h6" color="error">
            {intl.formatMessage({ id: noAccessMessage })}
          </Typography>
          <button
            className='cancelBtnStyle'
            style={{ marginTop: '20px' }}
            onClick={() => history.push(backUrl.replace(':id', id).replace(':org_id', org_id).replace(':bank_id', bank_id))}
          >
            {intl.formatMessage({ id: 'Back' })}
          </button>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ marginTop: -5, padding: 4 }}>
        <Typography sx={{ color: '#252733', fontWeight: 'bold', marginBottom: '16px' }}>
          {intl.formatMessage({ id: pageTitle })}
        </Typography>

        {isFormLoading ? (
          <CircularProgress style={{ display: 'block' }} />
        ) : (
          <Box component="form" onSubmit={handleSubmit}>
            {/* Render hidden inputs separately */}
            {hiddenFields.map((field) => (
              <input
                key={field.name}
                type="hidden"
                id={field.name}
                value={formValues[field.name] || ''}
              />
            ))}

            {renderAboveFields && renderAboveFields(formValues, setFormValues, intl, dropdownOptions)}

            {/* Render fields in columns */}
            <div style={{ display: 'flex', gap: '100px', flexWrap: 'wrap' }}>
              {columns.map((column, colIndex) => (
                <div key={colIndex} style={{ flex: '0 0 auto', width: '300px' }}>
                  {column.map((field) => {
                  // Handle visibility
                  if (field.visibleIf && !field.visibleIf(formValues)) return null;

                  if (field.type === 'divider') {
                    return (
                      <Box key={field.name || Math.random()} sx={{ my: 2 }}>
                         {field.labelKey && (
                           <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#252733' }}>
                             {intl.formatMessage({ id: field.labelKey })}
                           </Typography>
                         )}
                         <Divider />
                      </Box>
                    );
                  }

                  if (field.type === 'hidden') {
                    return null;
                  }

                  if (field.type === 'checkbox') {
                    return (
                      <FormControlLabel
                        key={field.name}
                        control={
                          <Checkbox
                            checked={!!formValues[field.name]}
                            onChange={(e) => handleInputChange(field.name, e.target.checked)}
                            sx={{ color: '#FF6B38', '&.Mui-checked': { color: '#FF6B38' } }}
                          />
                        }
                        label={intl.formatMessage({ id: field.labelKey })}
                        sx={{ mb: 2, display: 'block', ...field.sx }}
                        required={field.required}
                      />
                    );
                  }

                  if (field.type === 'radioGroup') {
                     return (
                       <Box key={field.name} sx={{ mb: 2 }}>
                         <label className='labelStyle'>{intl.formatMessage({ id: field.labelKey })}</label>
                         <RadioGroup
                           row
                           name={field.name}
                           value={formValues[field.name] === null || formValues[field.name] === undefined ? (field.defaultValue || '') : formValues[field.name]}
                           onChange={(e) => handleInputChange(field.name, e.target.value)}
                         >
                           {field.options.map(opt => (
                             <FormControlLabel
                               key={opt.value}
                               value={opt.value}
                               disabled={typeof field.disabled === 'function' ? field.disabled(formValues) : !!field.disabled}
                               control={<Radio size="small" sx={{ color: '#FF6B38', '&.Mui-checked': { color: '#FF6B38' } }} />}
                               label={intl.formatMessage({ id: opt.labelKey })}
                             />
                           ))}
                         </RadioGroup>
                         {field.warnMessage && field.warnMessage(formValues) && (
                           <div style={{ color: '#d48806', fontSize: '0.75rem', marginTop: '4px', maxWidth: '270px', lineHeight: '1.2' }}>
                             {intl.formatMessage({ id: field.warnMessage(formValues) })}
                           </div>
                         )}
                       </Box>
                     );
                  }

                  if (field.type === 'checkboxGroup') {
                    return (
                      <Box key={field.name} sx={{ mb: 2 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          {field.labelKey && <label className='labelStyle'>{intl.formatMessage({ id: field.labelKey })}</label>}
                          {field.renderExtra && field.renderExtra(formValues[field.name], null, intl, setFormValues, formValues)}
                        </div>
                        <FormGroup sx={{ mt: 0 }}>
                          <Grid container>
                            {field.options.map(opt => (
                              <Grid item xs={6} key={opt.name}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={!!formValues[opt.name]}
                                      onChange={(e) => handleInputChange(opt.name, e.target.checked)}
                                      sx={{ color: '#FF6B38', '&.Mui-checked': { color: '#FF6B38' } }}
                                    />
                                  }
                                  label={intl.formatMessage({ id: opt.labelKey })}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </FormGroup>
                      </Box>
                    );
                  }

                  if (field.type === 'custom') {
                    if (field.render) {
                      return <React.Fragment key={field.name}>{field.render(formValues, setFormValues, intl, dropdownOptions)}</React.Fragment>;
                    }
                    return null;
                  }

                  if (field.type === 'dropdown') {
                    // Dropdown field
                    return (
                      <FormControl key={field.name} style={{ marginBottom: '16px', width: '100%' }}>
                        <label className='labelStyle'>
                          {intl.formatMessage({ id: field.labelKey })}
                          {field.required && <span style={{ color: 'red' }}> * </span>}
                        </label>

                        {dropdownLoading[field.name] ? (
                          <CircularProgress size={24} style={{ display: 'block' }} />
                        ) : (
                          <select
                            className='inputStyle'
                            id={field.name}
                            value={formValues[field.name] === null || formValues[field.name] === undefined ? '' : formValues[field.name]}
                            onChange={(e) => handleInputChange(field.name, e.target.value)}
                            required={field.required}
                            disabled={field.disabled || (field.dependsOn && !formValues[field.dependsOn])}
                          >
                            {!field.includeNoneOption && (
                              <option value="" disabled>
                                {field.dependsOn && !formValues[field.dependsOn]
                                  ? (intl.messages['SelectDependencyFirst'] ? intl.formatMessage({ id: 'SelectDependencyFirst' }) : 'Please select the required field first')
                                  : (intl.messages['SelectOption'] ? intl.formatMessage({ id: 'SelectOption' }) : 'Select an option')}
                              </option>
                            )}
                            {field.includeNoneOption && (
                              <option value="">
                                {field.noneOptionLabelKey
                                  ? intl.formatMessage({ id: field.noneOptionLabelKey })
                                  : (currentLanguageCode === 'en-us' ? 'None' : 'لا يوجد')}
                              </option>
                            )}
                            {(dropdownOptions[field.name] || []).map((option) => {
                              const value = option[field.optionConfig.valueKey];
                              const label = currentLanguageCode === 'en-us'
                                ? option[field.optionConfig.labelKey]
                                : option[field.optionConfig.labelKeyAr];

                              return (
                                <option key={value} value={value}>
                                  {label}
                                </option>
                              );
                            })}
                          </select>
                        )}
                        {field.renderExtra && field.renderExtra(formValues[field.name], dropdownOptions[field.name] || [], intl, setFormValues, formValues)}
                      </FormControl>
                    );
                  }

                  // Default input field
                  return (
                    <div key={field.name} style={{ marginBottom: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
                        <label className='labelStyle' style={{ margin: 0 }}>
                          {intl.formatMessage({ id: field.labelKey })}
                        </label>
                        {field.required && <span style={{ color: 'red' }}>*</span>}
                        {field.hint && (
                          <span
                            onMouseEnter={() => setHoveredHint(field.name)}
                            onMouseLeave={() => setHoveredHint(null)}
                            style={{ position: 'relative', display: 'inline-flex', cursor: 'help' }}
                          >
                            <span style={{ fontSize: '11px', color: '#1890ff', border: '1px solid #1890ff', borderRadius: '50%', width: '15px', height: '15px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', lineHeight: 1, flexShrink: 0 }}>?</span>
                            {hoveredHint === field.name && (
                              <div style={{ position: 'absolute', top: 'calc(100% + 8px)', left: '50%', transform: 'translateX(-50%)', backgroundColor: '#fff', color: '#000', padding: '10px 14px', borderRadius: '8px', fontSize: '12px', lineHeight: '1.6', width: '280px', zIndex: 9999, boxShadow: '0 6px 20px rgba(0,0,0,0.15)', border: '1px solid #d9d9d9', direction: intl.locale === 'ar-eg' ? 'rtl' : 'ltr', textAlign: intl.locale === 'ar-eg' ? 'right' : 'left', whiteSpace: 'pre-line' }}>
                                {intl.formatMessage({ id: field.hint })}
                                <div style={{ position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)', border: '6px solid transparent', borderBottomColor: '#d9d9d9' }} />
                                <div style={{ position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)', border: '5px solid transparent', borderBottomColor: '#fff', marginBottom: '-1px' }} />
                              </div>
                            )}
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <input
                          type={field.type === 'date' ? (focusedField === field.name ? 'date' : 'text') : (field.type || 'text')}
                          placeholder={field.type === 'date' && intl.locale === 'ar-eg' ? 'ي/ش/س' : (field.type === 'date' ? 'dd/mm/yyyy' : '')}
                          className='inputStyle'
                          id={field.name}
                          disabled={typeof field.disabled === 'function' ? field.disabled(formValues) : field.disabled}
                          value={
                            field.type === 'date' && formValues[field.name] && focusedField !== field.name
                              ? (() => { const p = (formValues[field.name] || '').split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : (formValues[field.name] || ''); })()
                              : (formValues[field.name] || '')
                          }
                          onChange={(e) => { if (field.type !== 'date' || focusedField === field.name) handleInputChange(field.name, e.target.value); }}
                          onFocus={() => { if (field.type === 'date') setFocusedField(field.name); }}
                          onBlur={() => { if (field.type === 'date') setFocusedField(null); }}
                          required={field.required}
                          maxLength={field.maxLength}
                          minLength={field.minLength}
                          style={{ 
                            flex: (field.renderExtra && !field.style?.width) ? 1 : 'none', 
                            color: !formValues[field.name] ? 'gray' : 'black',
                            direction: intl.locale === 'ar-eg' ? 'rtl' : 'ltr',
                            textAlign: intl.locale === 'ar-eg' ? 'right' : 'left',
                            ...field.style 
                          }}
                        />
                        {field.renderExtra && field.renderExtra(formValues[field.name], null, intl, setFormValues, formValues)}
                      </div>
                      {field.warnMessage && field.warnMessage(formValues) && (
                        <div style={{ color: '#d48806', fontSize: '0.75rem', marginTop: '4px', maxWidth: '270px', lineHeight: '1.2' }}>
                          {intl.formatMessage({ id: field.warnMessage(formValues) })}
                        </div>
                      )}
                    </div>
                  );
                })}
                </div>
              ))}
            </div>

            {renderAboveSubmit && renderAboveSubmit(formValues, setFormValues, intl, dropdownOptions)}

            {/* Submit and Cancel buttons */}
            <div style={{ marginTop: '-10px', marginBottom: '20px', display: 'block' }}>
              <button
                type="submit"
                className='updateBtnStyle'
                disabled={isLoading}
              >
                {isLoading
                  ? intl.formatMessage({ id: 'Updating' })
                  : intl.formatMessage({ id: updateButtonLabel })}
              </button>
              <button
                type="button"
                className='cancelBtnStyle'
                onClick={() => {
                  let navigateUrl = backUrl
                    .replace(':id', id)
                    .replace(':org_id', org_id)
                    .replace(':bank_id', bank_id);

                  if (came_from === "one") {
                    history.push(navigateUrl);
                  } else {
                    const urlParts = navigateUrl.split('/');
                    urlParts.pop();
                    history.push(urlParts.join('/'));
                  }
                }}
              >
                {intl.formatMessage({ id: 'Cancel' })}
              </button>
            </div>
          </Box>
        )}
      </Box>
    </>
  );
};

export default EditEntity;
